import { useMemo } from 'react'
import { MoreHorizontal, Clock } from 'lucide-react'
import dayjs from '@dcp/shared/utils/dayjs'
import {
  Button,
  Badge,
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
  Skeleton,
  DataTable,
  StatusBadge,
  EmptyState,
  type DataTableColumn
} from '@atrvd/saas-ui'
import type { CrossCheckingTransaction } from '@/types/banking'
import { formatCurrency } from '@dcp/shared'
import { cn } from '@atrvd/saas-ui'
import { useTableColumnPrefs } from '@/hooks/useTableColumnPrefs'

interface CrossCheckingTableProps {
  transactions: CrossCheckingTransaction[]
  isLoading?: boolean
}

/**
 * Data table for cross-checking transactions with horizontal scroll
 * Displays all transaction columns in a single scrollable row
 */
export function CrossCheckingTable({
  transactions,
  isLoading,
}: CrossCheckingTableProps) {
  const columns = useMemo<DataTableColumn<CrossCheckingTransaction>[]>(
    () => {
      /**
       * Renders status badge for Sage sync status
       * @param status - Current sync status of the transaction
       */
      const renderRecordInSageStatus = (status?: 'synced' | 'pending' | 'failed') => {
        if (status === 'synced') {
          return <StatusBadge status="success" label="Synced" />
        }
        if (status === 'pending') {
          return <StatusBadge status="pending" label="Pending" />
        }
        if (status === 'failed') {
          return <StatusBadge status="error" label="Failed" />
        }
        return null
      }

      return [
      {
        key: 'facility',
        header: 'Facility',
        sortable: true,
        render: (row) => (
          <div className="font-medium">{row.facility}</div>
        ),
      },
      {
        key: 'bank',
        header: 'Bank',
        render: (row) => <div>{row.bank}</div>,
      },
      {
        key: 'accountName',
        header: 'Account name',
        render: (row) => (
          <div className="text-blue-600 underline cursor-pointer">
            {row.accountName}
          </div>
        ),
      },
      {
        key: 'accountNumber',
        header: 'Acct number',
        render: (row) => <div>{row.accountNumber}</div>,
      },
      {
        key: 'date',
        header: 'Transaction date',
        sortable: true,
        render: (row) => {
          const dateStr = row.date
          const d = dayjs(dateStr)
          if (!dateStr || !d.isValid()) {
            return <div>-</div>
          }
          return <div>{d.format('MM/DD/YYYY')}</div>
        },
      },
      {
        key: 'transactionNumber',
        header: 'Transaction number',
        disableHide: true,
        render: (row) => <div>{row.transactionNumber}</div>,
      },
      {
        key: 'category',
        header: 'Transaction category',
        render: (row) => (
          <Badge variant="outline" className="font-normal">
            {row.category.replace(/_/g, ' ')}
          </Badge>
        ),
      },
      {
        key: 'transactionStatus',
        header: 'Bank transaction category',
        render: (row) => <div>{row.transactionStatus}</div>,
      },
      {
        key: 'description',
        header: 'Description',
        render: (row) => (
          <div className="min-w-[150px]">{row.description}</div>
        ),
      },
      {
        key: 'amount',
        header: 'Transaction Amount',
        sortable: true,
        render: (row) => (
          <div
            className={cn(
              'text-right font-medium',
              row.amount < 0 && 'text-destructive'
            )}
          >
            {formatCurrency(row.amount)}
          </div>
        ),
      },
      {
        key: 'paymentInfo',
        header: 'Payment information',
        render: (row) => <div>{row.paymentInfo || '****'}</div>,
      },
      {
        key: 'notes',
        header: 'Notes',
        render: (row) => <div>{row.notes || '-'}</div>,
      },
      {
        key: 'recordInSageStatus',
        header: 'Record in Sage status',
        render: (row) => (
          <div className="flex items-center justify-center">
            {renderRecordInSageStatus(row.recordInSageStatus)}
          </div>
        ),
      },
      {
        key: 'actions',
        header: '',
        disableHide: true,
        render: () => (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem>View details</DropdownMenuItem>
              <DropdownMenuItem>Edit</DropdownMenuItem>
              <DropdownMenuItem className="text-destructive">
                Delete
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        ),
      },
    ]
    },
    []
  )

  // Per-user persisted column visibility + order for this table.
  const columnPrefs = useTableColumnPrefs('cross-checking', columns)

  if (isLoading) {
    return (
      <div className="space-y-4">
        <Skeleton className="h-[400px] w-full" />
      </div>
    )
  }

  return (
    <DataTable
      columns={columns}
      data={transactions}
      title="Transactions Status in Sage"
      itemName="transactions"
      sorting={{ mode: 'client' }}
      loading={isLoading}
      loadingComponent={<Skeleton className="h-[400px] w-full" />}
      emptyState={
        <EmptyState
          icon={Clock}
          title="No transactions found"
        />
      }
      enableColumnReorder
      {...columnPrefs}
    />
  )
}
